import { Divider, Table } from 'antd';
import moment from 'moment';
import { useRouteMatch } from 'react-router-dom';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import SyntheticAnalysisFilter from '@/components/CRM/SearchQuestionnaire/SyntheticAnalysisFilter';
import { useAnswersCustomers } from '@/core/domain/crm/Answers/Answers.hooks';
import {
  AnswerCustomer,
  AnswerCustomerContact,
} from '@/core/domain/crm/Answers/Answers.types';
import { applyCpfCnpjMask } from '@/utils/mask';
import EyeIcon from '@/components/_common/EyeIcon';
import AnswerCustomerModal from '@/components/CRM/SearchQuestionnaire/AnswerCustomerModal';
import { useDisclosure } from '@/hooks';
import { ContactType } from '@/core/domain/_shared/ContactType';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ButtonBack from '@/components/_common/ButtonBack';

const IndividualAnalysisPage: React.FC = () => {
  const intl = useIntl();
  const { quizId } = useRouteMatch<{ quizId: string }>().params;
  const [filter, setFilter] = useState<{
    startDate: moment.Moment;
    endDate: moment.Moment;
  }>({
    startDate: moment(),
    endDate: moment(),
  });
  const { data, isLoading } = useAnswersCustomers(Number(quizId), {
    startReplyDate: filter.startDate.format('YYYY-MM-DD'),
    endReplyDate: filter.endDate.format('YYYY-MM-DD'),
  });
  const [clientSelected, setClientSelected] = useState<AnswerCustomer>();
  const [modalOpened, modalAction] = useDisclosure(false, {
    onClose: () => setClientSelected(undefined),
  });

  const toggleClientModal = (client?: AnswerCustomer) => {
    setClientSelected(client);
    modalAction.open();
  };

  return (
    <>
      <h2 style={{ marginBottom: 24 }}>
        {intl.formatMessage({
          id: 'layout.menu.crm.searchQuestionnaire.details.individualAnalysis',
        })}
      </h2>

      <SyntheticAnalysisFilter values={filter} onFilter={setFilter} />

      <Divider />

      <Table
        loading={isLoading}
        dataSource={data}
        scroll={{ x: true }}
        pagination={false}
        columns={[
          {
            key: 'name',
            title: intl.formatMessage({
              id: 'searchQuestionnaire.replayDate.list.customer',
            }),
            dataIndex: 'name',
            ellipsis: true,
            width: 160,
          },
          {
            key: 'contact',
            title: 'E-mail',
            dataIndex: 'customerContact',
            render: (contacts?: AnswerCustomerContact[]) =>
              contacts?.find(
                (contact) => contact.contactType === ContactType.Email,
              )?.value,
            ellipsis: true,
            width: 160,
          },
          {
            key: 'cpfCnpj',
            title: 'Nº Documento',
            dataIndex: 'cpfCnpj',
            ellipsis: true,
            render: (value) => applyCpfCnpjMask(value),
            width: 160,
          },
          {
            key: 'action',
            title: intl.formatMessage({
              id: 'searchQuestionnaire.replayDate.list.action',
            }),
            align: 'center',
            width: 50,
            ellipsis: true,
            render: (record) => (
              <EyeIcon
                iconProps={{ onClick: () => toggleClientModal(record) }}
              />
            ),
          },
        ]}
      />

      <Divider />

      <SpaceForButton>
        <ButtonBack path="/app/customers/crm/searchQuestionnaire" />
      </SpaceForButton>

      <AnswerCustomerModal
        open={modalOpened}
        onCancel={modalAction.close}
        customer={clientSelected}
        params={{
          startReplyDate: filter.startDate.format('YYYY-MM-DD'),
          endReplyDate: filter.endDate.format('YYYY-MM-DD'),
        }}
      />
    </>
  );
};

export default IndividualAnalysisPage;
